'use client';

import type { DimensionFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useVariationFormattedLink } from '@packages/modules/src/VariationSelection/utils/useVariationFormattedLink';
import { useImageGalleryColorDialogTracking } from '../useImageGalleryColorDialogTracking/useImageGalleryColorDialogTracking';

export type ImageGalleryColorDialogItemProps = {
  /**
   * The dimension value to display.
   */
  value: DimensionFragmentFragment['values'][number];

  /**
   * Select handler. Call when the variation has been selected (clicked)
   *
   * @param value Dimension value.
   */
  onSelectCallback(): void;
};

export const ImageGalleryColorDialogItem = ({
  value: { sku, displayText, isSelected, rgb, variationMainImage },
  onSelectCallback,
}: ImageGalleryColorDialogItemProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const { push } = useRouter();
  const { createLink } = useVariationFormattedLink();
  const { trackVariationSelected } = useImageGalleryColorDialogTracking();

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.focus();
    }
  }, [isSelected]);

  const handleSelect = () => {
    if (sku) {
      trackVariationSelected();
      push(createLink(sku));
    }
    onSelectCallback();
  };

  return (
    <button
      ref={ref}
      type="button"
      tabIndex={0}
      onClick={handleSelect}
      // m-[2px] -> the horziontal scroll component will cut off the box shadow otherwise, caused by overflow-hidden
      className={`border-color-text-dark shadow-color-grey-dark m-[2px] flex w-[96px] min-w-[96px] flex-col justify-stretch overflow-hidden rounded-sm border-solid shadow-xs ${isSelected ? 'border-2' : 'border-none'}`}
    >
      <span className="relative block h-[96px] w-[96px]">
        {variationMainImage ? (
          /* TODO: where do we get an alt text from? */
          <Image fill src={`${variationMainImage}?$PDV_gallery$`} alt="" role="presentation" />
        ) : (
          rgb && (
            <div
              title={displayText || ''}
              className="h-full w-full"
              style={{ backgroundColor: `#${rgb}` }}
            />
          )
        )}
      </span>
      <span className="text-size-body-xs block px-1 py-1.5 text-center hyphens-auto">
        {displayText}
      </span>
    </button>
  );
};
